import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Moon, Sun, Bell, LogOut, User, Palette, Monitor, ChevronRight, FileText, Shield, ExternalLink, Check, Loader2, Pencil, BookOpen, CreditCard, Download } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import tokenStore from '../../utils/tokenStore';
import { API_URL } from '../../constants/api';
import { getVersionParts } from '../../utils/version';
import { CURRENCIES, DEFAULT_CURRENCY } from '../../constants/currencies';
import { BUILD_TIME_OPTIONS } from '../../constants/buildTimes';
import { getConsent, setConsent } from '../../utils/analytics';

const API = `${API_URL}/api`;

/* ------------------------------------------------------------------ */
/*  Reusable primitives                                                */
/* ------------------------------------------------------------------ */

/** Section card with optional header */
const Section = ({ title, children, danger }) => (
  <div
    className="rounded-xl overflow-hidden"
    style={{ border: '1px solid var(--card-border)' }}
  >
    {title && (
      <div
        className="px-4 py-3 text-xs font-semibold tracking-wide uppercase"
        style={{
          backgroundColor: 'var(--bg-base)',
          color: danger ? '#ef4444' : 'var(--text-muted)',
          borderBottom: '1px solid var(--card-border)',
        }}
      >
        {title}
      </div>
    )}
    <div style={{ backgroundColor: 'var(--card-bg-solid)' }}>
      {children}
    </div>
  </div>
);

/** Single row inside a Section — 48px touch target on mobile, normal on desktop */
const Row = ({ label, desc, right, onClick, danger, icon: Icon }) => {
  const Tag = onClick ? 'button' : 'div';
  const interactiveProps = onClick
    ? {
        type: 'button',
        onClick,
        onMouseEnter: e => { e.currentTarget.style.backgroundColor = 'var(--interactive-hover)'; },
        onMouseLeave: e => { e.currentTarget.style.backgroundColor = 'transparent'; },
      }
    : {};
  return (
    <Tag
      {...interactiveProps}
      className="w-full flex items-center gap-3 px-4 min-h-[48px] sm:min-h-0 py-3 sm:py-3.5 text-left transition-colors"
      style={{
        borderBottom: '1px solid var(--card-border)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {Icon && (
        <Icon
          className="w-[18px] h-[18px] sm:w-4 sm:h-4 flex-shrink-0"
          style={{ color: danger ? '#ef4444' : 'var(--text-muted)' }}
        />
      )}
      <div className="flex-1 min-w-0">
        <p
          className="text-[13px] sm:text-sm font-medium leading-tight sm:leading-normal sm:truncate"
          style={{ color: danger ? '#ef4444' : 'var(--text-primary)' }}
        >
          {label}
        </p>
        {desc && (
          <p className="text-[11px] sm:text-xs mt-0.5 leading-tight sm:leading-normal sm:truncate" style={{ color: 'var(--text-muted)' }}>
            {desc}
          </p>
        )}
      </div>
      {right || (onClick && (
        <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
      ))}
    </Tag>
  );
};

/** Toggle switch — 44px wide touch area */
const Toggle = ({ enabled, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className="relative rounded-full transition-colors flex-shrink-0"
    style={{
      width: '44px', height: '24px',
      backgroundColor: enabled ? 'var(--accent-primary)' : 'var(--interactive-default)',
      border: '1px solid var(--border-default)',
    }}
  >
    <div
      className="absolute rounded-full transition-transform shadow-sm"
      style={{
        top: '2px', width: '18px', height: '18px',
        backgroundColor: '#fff',
        transform: enabled ? 'translateX(22px)' : 'translateX(3px)',
      }}
    />
  </button>
);

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const SidebarSettings = ({ isOpen, onClose, currentUser, onLogout, onShowLegal, onProfileUpdate, onShowPricing }) => {
  const { theme, setTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('general');

  // Account form state
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'ok' | 'err' | null
  const [usernameError, setUsernameError] = useState('');

  // Change password modal state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Usage analytics (PostHog) — same choice as the consent banner, changeable here.
  const [analyticsOn, setAnalyticsOn] = useState(() => getConsent() === 'granted');

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Export state (GDPR Art. 15 / 20 — a copy of your own data)
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const handleExportData = async () => {
    setIsExporting(true);
    setExportError('');
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API}/auth/export-data`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not prepare your data export');
      }
      // Hand the file to the browser; nothing is stored on our side.
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `foundersplus-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err.message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setIsDeleting(true);
    setDeleteError('');
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API}/auth/delete-account`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to delete account');
      }
      tokenStore.clear();
      sessionStorage.removeItem('refresh_token');
      window.location.href = '/';
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Sync form when user data or tab changes
  React.useEffect(() => {
    if (currentUser && activeTab === 'account') {
      setUsername(currentUser.username || '');
      setBio(currentUser.bio || '');
      setSaveStatus(null);
      setUsernameError('');
    }
  }, [currentUser, activeTab]);

  const handleSaveProfile = async () => {
    const trimmedUsername = username.trim();
    if (!trimmedUsername) return;
    if (!/^[a-zA-Z0-9_]+$/.test(trimmedUsername)) {
      setUsernameError('Username may only contain letters, numbers, and underscores');
      return;
    }
    setUsernameError('');
    setIsSaving(true);
    setSaveStatus(null);
    try {
      const token = tokenStore.get();
      const payload = { bio: bio.trim() };
      if (trimmedUsername !== currentUser?.username) {
        payload.username = trimmedUsername;
        payload.full_name = trimmedUsername;
      }
      const res = await fetch(`${API}/users/me/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (res.status === 409) {
        setUsernameError('Username already taken — please choose another');
        return;
      }
      if (!res.ok) throw new Error('save failed');
      const updated = await res.json();
      setSaveStatus('ok');
      onProfileUpdate?.({
        full_name: updated.full_name,
        username: updated.username,
        bio: updated.bio,
      });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch {
      setSaveStatus('err');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setPasswordError('Password must contain an uppercase letter');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setPasswordError('Password must contain a lowercase letter');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setPasswordError('Password must contain a number');
      return;
    }
    if (!/[^A-Za-z0-9]/.test(newPassword)) {
      setPasswordError('Password must contain a special character (!@#$%^&* etc.)');
      return;
    }

    setIsChangingPassword(true);
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPasswordError(data.detail || 'Failed to change password');
        return;
      }
      setPasswordSuccess(data.message || 'Password changed successfully!');
      setTimeout(() => {
        setShowChangePasswordModal(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
        setPasswordSuccess('');
      }, 2000);
    } catch (err) {
      setPasswordError(err.message || 'An error occurred');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const closeChangePasswordModal = () => {
    setShowChangePasswordModal(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  // Email preferences (daily AI brief)
  const browserTz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
    catch { return 'UTC'; }
  })();
  const [emailPrefs, setEmailPrefs] = useState({
    daily_brief: false,
    timezone: browserTz,
    send_hour_local: 8,
  });
  const [emailPrefsSaving, setEmailPrefsSaving] = useState(false);
  const [showBriefDetails, setShowBriefDetails] = useState(false);

  // Sync email prefs from currentUser
  React.useEffect(() => {
    const p = currentUser?.email_preferences;
    if (p) {
      // If the stored timezone is UTC or missing, fall back to the browser's
      // real timezone — prevents users in IST/PST/etc from getting email at
      // the wrong hour just because the default was never overwritten.
      const storedTz = p.timezone;
      const effectiveTz = (storedTz && storedTz !== 'UTC') ? storedTz : browserTz;
      setEmailPrefs({
        daily_brief: !!p.daily_brief,
        timezone: effectiveTz,
        send_hour_local: typeof p.send_hour_local === 'number' ? p.send_hour_local : 8,
        build_time: p.build_time || '',
      });
    }
    // eslint-disable-next-line
  }, [currentUser?.email_preferences]);

  // "When do you usually work?" — sets the brief hour too (backend decides it).
  const saveBuildTime = async (value) => {
    if (!value) return;
    setEmailPrefsSaving(true);
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API}/users/me/build-time`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ build_time: value }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const p = data.email_preferences || {};
      setEmailPrefs(prev => ({ ...prev, build_time: p.build_time, send_hour_local: p.send_hour_local }));
      onProfileUpdate?.({ email_preferences: p });
    } catch (err) {
      console.error('Failed to save work time', err);
    } finally {
      setEmailPrefsSaving(false);
    }
  };

  const saveEmailPrefs = async (next) => {
    setEmailPrefs(next);
    setEmailPrefsSaving(true);
    try {
      const token = tokenStore.get();
      await fetch(`${API}/users/me/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // Only the keys this form owns — the work-time answer is saved separately
        // (saveBuildTime) and the server merges, so it is never overwritten here.
        body: JSON.stringify({ email_preferences: {
          daily_brief: next.daily_brief, timezone: next.timezone, send_hour_local: next.send_hour_local,
        } }),
      });
    } catch (err) {
      // silent fail — UI already shows new state; user can retry
      console.error('Failed to save email preferences', err);
    } finally {
      setEmailPrefsSaving(false);
    }
  };

  // Plan & Billing state
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingActionLoading, setBillingActionLoading] = useState(false);
  const [billingError, setBillingError] = useState('');

  React.useEffect(() => {
    if (activeTab !== 'billing') return;
    setBillingLoading(true);
    setBillingError('');
    const token = tokenStore.get();
    const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    Promise.all([
      fetch(`${API}/billing/subscription`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/billing/plans`, { headers }).then(r => r.ok ? r.json() : []),
    ])
      .then(([sub, planList]) => {
        setSubscription(sub);
        setPlans(planList || []);
      })
      .catch(() => setBillingError('Could not load billing info'))
      .finally(() => setBillingLoading(false));
  }, [activeTab]);

  const handleManageBilling = async () => {
    setBillingActionLoading(true);
    setBillingError('');
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API}/billing/portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not open billing portal');
      window.location.href = data.url;
    } catch (err) {
      setBillingError(err.message);
      setBillingActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel your subscription at the end of the current billing period?')) return;
    setBillingActionLoading(true);
    setBillingError('');
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API}/billing/cancel-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not cancel subscription');
      setSubscription(prev => prev ? { ...prev, plan_status: 'cancelled' } : prev);
    } catch (err) {
      setBillingError(err.message);
    } finally {
      setBillingActionLoading(false);
    }
  };

  const tabs = [
    { id: 'general',       label: 'General',       icon: Palette },
    { id: 'account',       label: 'Account',       icon: User },
    { id: 'billing',       label: 'Billing',       icon: CreditCard },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  if (!isOpen) return null;

  const themes = [
    {
      id: 'dark',
      label: 'Dark',
      sub: 'GitHub-style',
      icon: Moon,
      accent: 'var(--accent-indigo, #6366f1)',
      preview: { bg: '#0d1117', sidebar: '#161b22', line1: 'rgba(255,255,255,0.12)', line2: 'rgba(255,255,255,0.06)', dot1: 'rgba(99,102,241,0.15)', dot2: 'rgba(16,185,129,0.15)' },
    },
    {
      id: 'blue',
      label: 'Blue',
      sub: 'Slate & teal',
      icon: Monitor,
      accent: 'var(--accent-emerald-text, #10b981)',
      preview: { bg: '#020617', sidebar: '#0f172a', line1: '#1e293b', line2: '#0f172a', dot1: 'rgba(16,185,129,0.2)', dot2: 'rgba(59,130,246,0.2)' },
    },
    {
      id: 'light',
      label: 'Light',
      sub: 'Clean & bright',
      icon: Sun,
      accent: '#f59e0b',
      preview: { bg: '#f8fafc', sidebar: '#ffffff', line1: '#e2e8f0', line2: '#f1f5f9', dot1: '#d1fae5', dot2: '#dbeafe', border: '#e2e8f0', sidebarBorder: '#e2e8f0' },
    },
  ];

  const renderContent = () => {
    switch (activeTab) {

      case 'general':
        return (
          <div className="space-y-5">
            <Section title="Appearance">
              <div className="p-3 sm:p-4 grid grid-cols-3 gap-2">
                {themes.map(t => {
                  const sel = theme === t.id;
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTheme(t.id)}
                      className="relative rounded-lg p-2 sm:p-2.5 text-left transition-all duration-150"
                      style={{
                        backgroundColor: sel ? 'var(--interactive-hover)' : 'transparent',
                        border: sel ? `2px solid ${t.accent}` : '2px solid var(--border-default)',
                      }}
                    >
                      <div
                        className="w-full aspect-[4/3] rounded-md mb-1.5 sm:mb-2 overflow-hidden"
                        style={{ backgroundColor: t.preview.bg, border: `1px solid ${t.preview.border || 'var(--card-border)'}` }}
                      >
                        <div className="flex h-full">
                          <div className="w-2 sm:w-3" style={{ backgroundColor: t.preview.sidebar, borderRight: `1px solid ${t.preview.sidebarBorder || 'var(--card-border)'}` }} />
                          <div className="flex-1 p-0.5 sm:p-1 space-y-0.5">
                            <div className="w-3/4 h-0.5 sm:h-1 rounded-full" style={{ backgroundColor: t.preview.line1 }} />
                            <div className="w-1/2 h-0.5 sm:h-1 rounded-full" style={{ backgroundColor: t.preview.line2 }} />
                            <div className="flex gap-0.5 pt-0.5">
                              <div className="w-2 sm:w-3 h-1.5 sm:h-2 rounded-sm" style={{ backgroundColor: t.preview.dot1 }} />
                              <div className="w-2 sm:w-3 h-1.5 sm:h-2 rounded-sm" style={{ backgroundColor: t.preview.dot2 }} />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon className="w-3 h-3 flex-shrink-0" style={{ color: sel ? t.accent : 'var(--text-muted)' }} />
                        <span className="text-[11px] sm:text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{t.label}</span>
                      </div>
                      <p className="text-[9px] sm:text-[10px] ml-4" style={{ color: 'var(--text-muted)' }}>{t.sub}</p>
                      {sel && (
                        <div className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.accent }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </Section>

            <Section title="Currency">
              <div className="px-3 sm:px-4 py-3">
                <select
                  className="w-full px-3 py-2.5 sm:py-2 rounded-lg text-[13px] sm:text-sm focus:outline-none focus:ring-1"
                  style={{
                    backgroundColor: 'var(--bg-base)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--text-primary)',
                  }}
                  value={currentUser?.currency || DEFAULT_CURRENCY}
                  onChange={async (e) => {
                    const code = e.target.value;
                    const token = tokenStore.get();
                    // Save first; only switch the app once the server has it, so the
                    // brief and email can never disagree with what the screens show.
                    try {
                      const res = await fetch(`${API}/users/me/profile`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                        body: JSON.stringify({ currency: code }),
                      });
                      if (!res.ok) throw new Error(`HTTP ${res.status}`);
                      onProfileUpdate?.({ currency: code });
                    } catch (err) {
                      console.warn('[Settings] currency save failed:', err);
                      window.alert("Couldn't save your currency. Please try again.");
                    }
                  }}
                >
                  {Object.entries(CURRENCIES).map(([code, { symbol, name }]) => (
                    <option key={code} value={code}>
                      {symbol} {code} — {name}
                    </option>
                  ))}
                </select>
                <p className="mt-1.5 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  Auto-detected from your location. Change anytime.
                </p>
              </div>
            </Section>
          </div>
        );

      case 'account':
        return (
          <div className="space-y-5">
            <Section title="Profile Information">
              <div className="p-3 sm:p-4 space-y-4">
                <div>
                  <label className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                    <Pencil className="w-3 h-3" />
                    Username
                  </label>
                  <input
                    className="w-full px-3 py-2.5 sm:py-2 rounded-lg text-[13px] sm:text-sm focus:outline-none focus:ring-1"
                    style={{
                      backgroundColor: 'var(--bg-base)',
                      border: `1px solid ${usernameError ? '#ef4444' : 'var(--border-default)'}`,
                      color: 'var(--text-primary)',
                    }}
                    value={username}
                    onChange={e => { setUsername(e.target.value); setUsernameError(''); }}
                    placeholder="e.g. shreyas"
                  />
                  {usernameError && (
                    <p className="text-[11px] mt-1" style={{ color: '#ef4444' }}>{usernameError}</p>
                  )}
                  {!username.trim() && (
                    <p className="text-[11px] mt-1" style={{ color: '#f59e0b' }}>Username is required — this is shown as your display name across the app</p>
                  )}
                  <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>Changing your username updates it on all your posts automatically.</p>
                </div>

                <FieldInput
                  label="Email"
                  defaultValue={currentUser?.email || ''}
                  disabled
                  hint="Email cannot be changed"
                />

                <div>
                  <label className="block text-[11px] sm:text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Bio</label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2.5 sm:py-2 rounded-lg text-[13px] sm:text-sm focus:outline-none focus:ring-1 resize-none"
                    style={{
                      backgroundColor: 'var(--bg-base)',
                      border: '1px solid var(--border-default)',
                      color: 'var(--text-primary)',
                    }}
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    placeholder="Tell us about yourself…"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSaveProfile}
                    disabled={isSaving || !username.trim()}
                    className="flex items-center gap-2 w-full sm:w-auto px-5 py-2.5 sm:py-2 rounded-lg text-[13px] sm:text-sm font-medium transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: 'var(--accent-primary)', color: '#fff' }}
                    onMouseEnter={e => { if (!isSaving) e.currentTarget.style.opacity = '0.85'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                  >
                    {isSaving ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
                    ) : saveStatus === 'ok' ? (
                      <><Check className="w-3.5 h-3.5" /> Saved</>
                    ) : 'Save Changes'}
                  </button>
                  {saveStatus === 'err' && (
                    <span className="text-[12px]" style={{ color: '#ef4444' }}>Save failed — try again</span>
                  )}
                </div>
              </div>
            </Section>

            <Section title="Password">
              <div className="p-3 sm:p-4">
                <button
                  onClick={() => setShowChangePasswordModal(true)}
                  className="w-full sm:w-auto px-5 py-2.5 sm:py-2 rounded-lg text-[13px] sm:text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: 'transparent',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-default)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--interactive-hover)'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  Change Password
                </button>
              </div>
            </Section>

            <Section title="Your Data">
              <Row
                icon={Shield}
                label="Usage analytics"
                desc={analyticsOn
                  ? 'On — we see whether you open your daily brief and what you tap (PostHog). Tap to turn off.'
                  : 'Off — nothing is tracked. Tap to let us see whether your daily brief helps.'}
                right={<span className="text-[12px] font-semibold" style={{ color: analyticsOn ? '#2dd4bf' : 'var(--text-muted)' }}>{analyticsOn ? 'On' : 'Off'}</span>}
                onClick={() => { const next = !analyticsOn; setConsent(next); setAnalyticsOn(next); }}
              />
              <Row
                icon={isExporting ? Loader2 : Download}
                label={isExporting ? 'Preparing your file…' : 'Download my data'}
                desc="A copy of everything we hold about you, as a JSON file"
                onClick={isExporting ? undefined : handleExportData}
              />
              {exportError && (
                <div className="px-3 sm:px-4 pb-3">
                  <p className="text-[12px] sm:text-xs" style={{ color: '#ef4444' }}>{exportError}</p>
                </div>
              )}
            </Section>

            <Section title="Legal">
              <Row icon={BookOpen} label="Get Started" desc="Learn how to use Founders+" onClick={() => { onClose(); onShowLegal?.('getstarted'); }} />
              <Row icon={Shield}   label="Privacy Policy" onClick={() => { onClose(); onShowLegal?.('privacy'); }} />
              <Row icon={FileText} label="Terms of Service" onClick={() => { onClose(); onShowLegal?.('terms'); }} />
              <Row icon={ExternalLink} label="Disclaimer" onClick={() => { onClose(); onShowLegal?.('disclaimer'); }} />
            </Section>

            <Section title="Danger Zone" danger>
              <div className="p-3 sm:p-4">
                {!showDeleteConfirm ? (
                  <button
                    onClick={() => { setShowDeleteConfirm(true); setDeleteConfirmText(''); setDeleteError(''); }}
                    className="w-full sm:w-auto px-5 py-2.5 sm:py-2 rounded-lg text-[13px] sm:text-sm font-medium transition-colors"
                    style={{
                      backgroundColor: 'rgba(239,68,68,0.08)',
                      color: '#ef4444',
                      border: '1px solid rgba(239,68,68,0.2)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.15)'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.08)'; }}
                  >
                    Delete Account
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-[13px] sm:text-sm font-medium" style={{ color: '#ef4444' }}>
                      This action is permanent. All your data will be deleted and cannot be recovered.
                    </p>
                    <p className="text-[12px] sm:text-xs" style={{ color: 'var(--text-muted)' }}>
                      Type <strong style={{ color: 'var(--text-primary)' }}>DELETE</strong> to confirm.
                    </p>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={e => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE"
                      className="w-full px-3 py-2 rounded-lg text-[13px] sm:text-sm"
                      style={{
                        backgroundColor: 'var(--bg-base)',
                        color: 'var(--text-primary)',
                        border: '1px solid rgba(239,68,68,0.3)',
                        outline: 'none',
                      }}
                      autoFocus
                    />
                    {deleteError && (
                      <p className="text-[12px]" style={{ color: '#ef4444' }}>{deleteError}</p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                        className="px-4 py-2 rounded-lg text-[13px] sm:text-sm font-medium transition-colors"
                        style={{
                          backgroundColor: deleteConfirmText === 'DELETE' ? '#ef4444' : 'rgba(239,68,68,0.2)',
                          color: deleteConfirmText === 'DELETE' ? '#fff' : 'rgba(239,68,68,0.5)',
                          cursor: deleteConfirmText === 'DELETE' && !isDeleting ? 'pointer' : 'not-allowed',
                          opacity: isDeleting ? 0.6 : 1,
                        }}
                      >
                        {isDeleting ? 'Deleting...' : 'Delete My Account'}
                      </button>
                      <button
                        onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setDeleteError(''); }}
                        className="px-4 py-2 rounded-lg text-[13px] sm:text-sm font-medium transition-colors"
                        style={{
                          backgroundColor: 'var(--bg-base)',
                          color: 'var(--text-secondary)',
                          border: '1px solid var(--border-default)',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </Section>
          </div>
        );

      case 'billing': {
        const planMeta = plans.find(p => p.id === subscription?.plan);
        const isFree = !subscription || subscription.plan === 'free';
        const isCancelling = subscription?.plan_status === 'cancelled';
        // Symbol comes from the plan catalog (backend/models/billing.py) so the
        // currency is defined in exactly one place; the literal is the fallback
        // for when the catalog hasn't loaded yet.
        const symbol = planMeta?.currency_symbol ?? '€';
        const formatPrice = (cents) => cents === 0 ? 'Free' : `${symbol}${(cents / 100).toFixed(0)}/mo`;
        return (
          <div className="space-y-5">
            <Section title="Current Plan">
              {billingLoading ? (
                <div className="px-4 py-6 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--text-muted)' }} />
                </div>
              ) : (
                <>
                  <Row
                    label={planMeta?.name || 'Free'}
                    desc={
                      isCancelling
                        ? 'Cancels at the end of the current billing period'
                        : subscription?.current_period_end
                          ? `Renews ${new Date(subscription.current_period_end).toLocaleDateString()}`
                          : 'No active subscription'
                    }
                    right={
                      <span
                        className="text-[11px] font-semibold px-2 py-1 rounded-full uppercase tracking-wide"
                        style={{
                          backgroundColor: isCancelling ? 'rgba(251,191,36,0.12)' : isFree ? 'var(--interactive-default)' : 'rgba(45,212,191,0.12)',
                          color: isCancelling ? '#f59e0b' : isFree ? 'var(--text-muted)' : 'var(--accent-primary)',
                        }}
                      >
                        {subscription?.plan_status || 'free'}
                      </span>
                    }
                  />
                  {subscription?.plan === 'team' && (
                    <Row
                      label="Seats"
                      desc={`${subscription.seats_purchased ?? 1} of 4 seats used`}
                    />
                  )}
                  <Row
                    label="Compare plans"
                    desc="See all Solo and Team plans"
                    icon={CreditCard}
                    onClick={() => onShowPricing?.()}
                  />
                  {!isFree && (
                    <Row
                      label="Manage billing"
                      desc="Update payment method, view invoices"
                      icon={ExternalLink}
                      onClick={handleManageBilling}
                      right={billingActionLoading ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--text-muted)' }} /> : undefined}
                    />
                  )}
                  {!isFree && !isCancelling && (
                    <Row
                      label="Cancel subscription"
                      desc="Downgrade to Free at period end"
                      danger
                      onClick={handleCancelSubscription}
                    />
                  )}
                </>
              )}
            </Section>

            {billingError && (
              <div className="p-3 rounded-lg text-[12px] sm:text-sm" style={{
                backgroundColor: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                color: '#ef4444',
              }}>
                {billingError}
              </div>
            )}

            {subscription && !subscription.billing_enabled && (
              <div className="p-3 rounded-lg text-[12px] sm:text-sm" style={{
                backgroundColor: 'rgba(251,191,36,0.08)',
                border: '1px solid rgba(251,191,36,0.25)',
                color: '#f59e0b',
              }}>
                Billing isn't fully set up yet — pricing and plan info shown here, but checkout will be enabled soon.
              </div>
            )}
          </div>
        );
      }

      case 'notifications':
        return (
          <div className="space-y-5">
            <Section title="Daily AI Brief">
              <Row
                label="Daily AI brief via email"
                desc={emailPrefs.daily_brief
                  ? (() => {
                      const h = emailPrefs.send_hour_local;
                      const ampm = h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`;
                      const tzShort = emailPrefs.timezone ? emailPrefs.timezone.split('/').pop().replace('_', ' ') : 'local time';
                      return `Sent at ${ampm} (${tzShort})`;
                    })()
                  : 'Receive a personalized morning brief by email'}
                right={
                  <div className="flex items-center gap-2">
                    <Toggle
                      enabled={emailPrefs.daily_brief}
                      onChange={() => {
                        // Always stamp the real browser timezone when toggling on
                        // so the scheduler uses the correct local hour, not UTC.
                        const next = {
                          ...emailPrefs,
                          daily_brief: !emailPrefs.daily_brief,
                          timezone: browserTz,
                        };
                        saveEmailPrefs(next);
                      }}
                    />
                    {emailPrefs.daily_brief && (
                      <button
                        onClick={() => setShowBriefDetails(v => !v)}
                        className="p-1 rounded transition-colors"
                        style={{ color: 'var(--text-muted)' }}
                        title={showBriefDetails ? 'Collapse schedule' : 'Expand schedule'}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14" height="14" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round"
                          style={{ transform: showBriefDetails ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    )}
                  </div>
                }
              />
              {emailPrefs.daily_brief && showBriefDetails && (
                <div
                  className="px-4 py-3 sm:py-3.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
                  style={{ borderBottom: '1px solid var(--card-border)' }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] sm:text-sm font-medium leading-tight" style={{ color: 'var(--text-primary)' }}>
                      Timezone
                    </p>
                    <p className="text-[11px] sm:text-xs mt-0.5 leading-tight" style={{ color: 'var(--text-muted)' }}>
                      Used to schedule your brief
                    </p>
                  </div>
                  <select
                    value={emailPrefs.timezone}
                    onChange={(e) => saveEmailPrefs({ ...emailPrefs, timezone: e.target.value })}
                    className="w-full sm:w-56 px-3 py-2.5 sm:py-1.5 rounded-lg text-[13px] sm:text-sm focus:outline-none focus:ring-1"
                    style={{
                      backgroundColor: 'var(--bg-base)',
                      border: '1px solid var(--border-default)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <optgroup label="🇮🇳 India / Nepal">
                      <option value="Asia/Kolkata">India (IST, UTC+5:30)</option>
                      <option value="Asia/Kathmandu">Nepal (NPT, UTC+5:45)</option>
                    </optgroup>
                    <optgroup label="🇬🇧 United Kingdom">
                      <option value="Europe/London">London (GMT/BST)</option>
                    </optgroup>
                    <optgroup label="🇺🇸 United States">
                      <option value="America/New_York">New York (ET, UTC-5/-4)</option>
                      <option value="America/Chicago">Chicago (CT, UTC-6/-5)</option>
                      <option value="America/Denver">Denver (MT, UTC-7/-6)</option>
                      <option value="America/Los_Angeles">Los Angeles (PT, UTC-8/-7)</option>
                    </optgroup>
                    <optgroup label="🇪🇺 Europe">
                      <option value="Europe/Paris">Paris / Berlin (CET, UTC+1/+2)</option>
                      <option value="Europe/Amsterdam">Amsterdam (CET, UTC+1/+2)</option>
                      <option value="Europe/Stockholm">Stockholm (CET, UTC+1/+2)</option>
                      <option value="Europe/Madrid">Madrid (CET, UTC+1/+2)</option>
                      <option value="Europe/Rome">Rome (CET, UTC+1/+2)</option>
                      <option value="Europe/Helsinki">Helsinki (EET, UTC+2/+3)</option>
                      <option value="Europe/Athens">Athens (EET, UTC+2/+3)</option>
                    </optgroup>
                    <optgroup label="🌏 Asia / Pacific">
                      <option value="Asia/Dubai">Dubai (GST, UTC+4)</option>
                      <option value="Asia/Singapore">Singapore (SGT, UTC+8)</option>
                      <option value="Asia/Tokyo">Tokyo (JST, UTC+9)</option>
                      <option value="Asia/Shanghai">Shanghai (CST, UTC+8)</option>
                      <option value="Australia/Sydney">Sydney (AEST, UTC+10/+11)</option>
                    </optgroup>
                    <optgroup label="🌍 Other">
                      <option value="UTC">UTC (no offset)</option>
                      <option value="America/Sao_Paulo">São Paulo (BRT, UTC-3)</option>
                      <option value="Africa/Lagos">Lagos (WAT, UTC+1)</option>
                      <option value="Africa/Nairobi">Nairobi (EAT, UTC+3)</option>
                    </optgroup>
                  </select>
                </div>
              )}
              {emailPrefs.daily_brief && showBriefDetails && (
                <div
                  className="px-4 py-3 sm:py-3.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] sm:text-sm font-medium leading-tight" style={{ color: 'var(--text-primary)' }}>
                      When do you work on your company?
                    </p>
                    <p className="text-[11px] sm:text-xs mt-0.5 leading-tight" style={{ color: 'var(--text-muted)' }}>
                      Your brief arrives about an hour before
                    </p>
                  </div>
                  <select
                    value={emailPrefs.build_time || ''}
                    onChange={(e) => saveBuildTime(e.target.value)}
                    className="w-full sm:w-40 px-3 py-2.5 sm:py-1.5 rounded-lg text-[13px] sm:text-sm focus:outline-none focus:ring-1"
                    style={{
                      backgroundColor: 'var(--bg-base)',
                      border: '1px solid var(--border-default)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <option value="" disabled>Not set</option>
                    {BUILD_TIME_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label} — {o.hint}</option>
                    ))}
                  </select>
                </div>
              )}
              {emailPrefs.daily_brief && showBriefDetails && (
                <div
                  className="px-4 py-3 sm:py-3.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] sm:text-sm font-medium leading-tight" style={{ color: 'var(--text-primary)' }}>
                      Send hour
                    </p>
                    <p className="text-[11px] sm:text-xs mt-0.5 leading-tight" style={{ color: 'var(--text-muted)' }}>
                      Local time (24h)
                    </p>
                  </div>
                  <select
                    value={emailPrefs.send_hour_local}
                    onChange={(e) => saveEmailPrefs({ ...emailPrefs, send_hour_local: parseInt(e.target.value, 10) })}
                    className="w-full sm:w-40 px-3 py-2.5 sm:py-1.5 rounded-lg text-[13px] sm:text-sm focus:outline-none focus:ring-1"
                    style={{
                      backgroundColor: 'var(--bg-base)',
                      border: '1px solid var(--border-default)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {Array.from({ length: 24 }, (_, h) => {
                      const ampm = h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`;
                      return (
                        <option key={h} value={h}>{String(h).padStart(2, '0')}:00 — {ampm}</option>
                      );
                    })}
                  </select>
                </div>
              )}
              {emailPrefsSaving && (
                <div className="px-4 py-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  Saving…
                </div>
              )}
            </Section>

          </div>
        );

      default:
        return null;
    }
  };

  return ReactDOM.createPortal(
    <>
      {/* ---- Change Password Modal ---- */}
      {showChangePasswordModal && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 9999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'settingsFadeIn 0.2s ease-out',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.55)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
            }}
            onClick={closeChangePasswordModal}
          />
          <div
            className="rounded-2xl flex flex-col shadow-2xl"
            style={{
              position: 'relative',
              zIndex: 10,
              width: '90vw',
              maxWidth: '420px',
              backgroundColor: 'var(--card-bg-solid)',
              border: '1px solid var(--card-border)',
              animation: 'settingsSlideUp 0.3s cubic-bezier(0.16,1,0.3,1)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 flex-shrink-0"
              style={{ borderBottom: '1px solid var(--card-border)' }}
            >
              <div>
                <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Change Password</h2>
                <p className="text-[11px] sm:text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Update your password securely</p>
              </div>
              <button
                onClick={closeChangePasswordModal}
                className="p-2 -mr-1 rounded-lg transition-colors"
                style={{ color: 'var(--text-muted)' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--interactive-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleChangePassword} className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-2 rounded-lg text-[13px] sm:text-sm focus:outline-none focus:ring-1"
                    style={{
                      backgroundColor: 'var(--bg-base)',
                      border: '1px solid var(--border-default)',
                      color: 'var(--text-primary)',
                    }}
                    required
                    autoComplete="current-password"
                  />
                </div>

                <div>
                  <label className="block text-[11px] sm:text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-2 rounded-lg text-[13px] sm:text-sm focus:outline-none focus:ring-1"
                    style={{
                      backgroundColor: 'var(--bg-base)',
                      border: '1px solid var(--border-default)',
                      color: 'var(--text-primary)',
                    }}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div>
                  <label className="block text-[11px] sm:text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-2 rounded-lg text-[13px] sm:text-sm focus:outline-none focus:ring-1"
                    style={{
                      backgroundColor: 'var(--bg-base)',
                      border: '1px solid var(--border-default)',
                      color: 'var(--text-primary)',
                    }}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-base)', border: '1px solid var(--card-border)' }}>
                  <p className="text-[11px] sm:text-xs font-medium mb-2" style={{ color: 'var(--text-muted)' }}>Password must contain:</p>
                  <ul className="text-[11px] sm:text-xs space-y-1" style={{ color: 'var(--text-muted)' }}>
                    <li>• At least 8 characters</li>
                    <li>• One uppercase letter (A-Z)</li>
                    <li>• One lowercase letter (a-z)</li>
                    <li>• One number (0-9)</li>
                    <li>• One special character (!@#$%^&*)</li>
                  </ul>
                </div>

                {passwordError && (
                  <div className="p-3 rounded-lg text-[12px] sm:text-sm" style={{
                    backgroundColor: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.3)',
                    color: '#ef4444',
                  }}>
                    {passwordError}
                  </div>
                )}

                {passwordSuccess && (
                  <div className="p-3 rounded-lg text-[12px] sm:text-sm flex items-center gap-2" style={{
                    backgroundColor: 'rgba(34,197,94,0.1)',
                    border: '1px solid rgba(34,197,94,0.3)',
                    color: '#22c55e',
                  }}>
                    <Check className="w-4 h-4 flex-shrink-0" />
                    {passwordSuccess}
                  </div>
                )}
              </div>
            </form>

            <div
              className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0"
              style={{ borderTop: '1px solid var(--card-border)' }}
            >
              <button
                onClick={closeChangePasswordModal}
                className="flex-1 px-4 py-2.5 sm:py-2 rounded-lg text-[13px] sm:text-sm font-medium transition-colors"
                style={{ backgroundColor: 'var(--interactive-default)', color: 'var(--text-primary)' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--interactive-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'var(--interactive-default)'; }}
              >
                Cancel
              </button>
              <button
                onClick={handleChangePassword}
                disabled={isChangingPassword || !!passwordSuccess}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 rounded-lg text-[13px] sm:text-sm font-medium transition-opacity disabled:opacity-50"
                style={{
                  backgroundColor: 'var(--accent-primary)',
                  color: '#fff',
                  cursor: isChangingPassword || passwordSuccess ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={e => { if (!isChangingPassword && !passwordSuccess) e.currentTarget.style.opacity = '0.85'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                {isChangingPassword ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Updating…</>
                ) : passwordSuccess ? (
                  <><Check className="w-3.5 h-3.5" /> Done</>
                ) : 'Update Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Main Settings Modal ---- */}
      <div
        className="sm:flex sm:items-center sm:justify-center"
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 999999,
          animation: 'settingsFadeIn 0.2s ease-out',
        }}
      >
        <style>{`
          @keyframes settingsFadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes settingsSlideUp { from { opacity: 0; transform: translateY(40px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        `}</style>
        <div
          className="hidden sm:block"
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          }}
          onClick={onClose}
        />
        <div
          className="flex flex-col h-full w-full sm:h-auto sm:w-[90vw] sm:max-w-3xl sm:rounded-2xl sm:shadow-2xl sm:max-h-[92vh] sm:min-h-[520px]"
          style={{
            position: 'relative',
            zIndex: 10,
            backgroundColor: 'var(--card-bg-solid)',
            border: 'none',
            borderColor: 'var(--card-border)',
          }}
          ref={el => { if (el && window.innerWidth >= 640) { el.style.border = '1px solid var(--card-border)'; el.style.animation = 'settingsSlideUp 0.3s cubic-bezier(0.16,1,0.3,1)'; } }}
          onClick={e => e.stopPropagation()}
        >
          <div
            className="flex items-center justify-between px-4 sm:px-6 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3 sm:py-5 flex-shrink-0"
            style={{ borderBottom: '1px solid var(--card-border)' }}
          >
            <div>
              <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Settings</h2>
              <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Manage your account and preferences</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 -mr-1 rounded-lg transition-colors"
              style={{ color: 'var(--text-muted)' }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--interactive-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              aria-label="Close settings"
            >
              <X className="w-5 h-5 sm:w-[18px] sm:h-[18px]" />
            </button>
          </div>

          <div
            className="flex gap-1 px-3 sm:px-5 py-2 sm:py-2.5 overflow-x-auto flex-shrink-0 scrollbar-hide"
            style={{ borderBottom: '1px solid var(--card-border)' }}
          >
            {tabs.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 sm:px-4 py-2 sm:py-2.5 rounded-lg text-[11px] sm:text-sm font-medium whitespace-nowrap transition-colors"
                  style={active ? {
                    backgroundColor: 'var(--accent-primary-bg)',
                    color: 'var(--accent-primary)',
                  } : {
                    color: 'var(--text-muted)',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = 'var(--interactive-hover)'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="flex-1 overflow-y-auto p-3 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
            {renderContent()}
          </div>

          <div
            className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0"
            style={{
              borderTop: '1px solid var(--card-border)',
              paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
            }}
          >
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] sm:text-sm font-medium transition-colors"
              style={{ color: '#ef4444' }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <LogOut className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
              Log Out
            </button>
            <button
              onClick={onClose}
              className="px-5 sm:px-5 py-2 sm:py-2 rounded-lg text-[13px] sm:text-sm font-medium transition-opacity"
              style={{ backgroundColor: 'var(--accent-primary)', color: '#fff' }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            >
              Done
            </button>
          </div>

          {/* Build identifier — click to copy. Used to pin a user complaint
              to an exact deploy when triaging Sentry / feedback. */}
          <VersionFooter />
        </div>
      </div>
    </>,
    document.body
  );
};

/* ------------------------------------------------------------------ */
/*  Field helpers                                                      */
/* ------------------------------------------------------------------ */

const FieldInput = ({ label, hint, disabled, ...rest }) => (
  <div>
    <label className="block text-[11px] sm:text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>{label}</label>
    <input
      className="w-full px-3 py-2.5 sm:py-2 rounded-lg text-[13px] sm:text-sm focus:outline-none focus:ring-1"
      style={{
        backgroundColor: disabled ? 'var(--interactive-default)' : 'var(--bg-base)',
        border: '1px solid var(--border-default)',
        color: disabled ? 'var(--text-muted)' : 'var(--text-primary)',
        cursor: disabled ? 'not-allowed' : undefined,
      }}
      disabled={disabled}
      {...rest}
    />
    {hint && <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>{hint}</p>}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Version footer — click to copy                                     */
/* ------------------------------------------------------------------ */
const VersionFooter = () => {
  const { semver, sha, buildDate, full } = getVersionParts();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (insecure context / permissions) — silently ignore.
    }
  };

  // Build the displayed string from whatever pieces we have.
  const parts = [`v${semver}`];
  if (sha) parts.push(sha);
  if (buildDate) parts.push(buildDate);

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Click to copy build identifier'}
      className="w-full text-center px-4 py-2 text-[10px] sm:text-[11px] font-mono transition-colors"
      style={{
        color: 'var(--text-muted)',
        borderTop: '1px solid var(--card-border)',
        cursor: 'pointer',
      }}
      onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
    >
      {copied ? 'Copied ✓' : parts.join(' · ')}
    </button>
  );
};

export default SidebarSettings;
